import type { ChangeEvent } from "react";

import { Button } from "@/components/ui/button";
import { columnKey } from "@/utils/csv";
import { Input, controlClass, controlHeight } from "@/components/ui/field";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/** How many rows a page of the review grid shows. */
export const PAGE_ROWS = 100;

/** Which rows a page number lands on, for the grid and for what Submit sends. */
export function pageSlice(rows: string[][], page: number) {
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_ROWS));
  const current = Math.min(page, pages);
  const start = (current - 1) * PAGE_ROWS;
  return { pages, current, start, shown: rows.slice(start, start + PAGE_ROWS) };
}

/**
 * The file, opened for correction before it is sent.
 *
 * A batch file is usually right and occasionally wrong in one cell, and the
 * round trip for that cell was: send it, read which rows the server rejected,
 * open the file again in a spreadsheet, find them, fix, re-send. This is the
 * same file with the fixing in the middle.
 *
 * A hundred rows to a page because the grid is inputs rather than text — a few
 * thousand of those at once is a page that stops responding to typing, which
 * is the one thing an editor may not do.
 */
/**
 * What a column's cells may hold, so the editor offers the right control: a
 * fixed choice becomes a select, a date a date field, and free text stays an
 * input with the keyboard hinted. The choices are the spellings the server
 * parses — offering anything else would be offering a rejection.
 */
/** Why a row was refused, and — when the server named one — in which column. */
export interface RowError {
  message: string;
  /** A `columnKey`-shaped column name, or null for a row-level problem. */
  field: string | null;
}

export type CellKind =
  | { kind: "options"; options: string[]; required?: boolean }
  | { kind: "date" }
  | { kind: "text"; inputMode?: "numeric" | "tel" | "email" };

export function CsvEditor({
  errors,
  header,
  rows,
  page,
  onPageChange,
  onChange,
  busy,
  types,
  readOnly = false,
}: {
  header: string[];
  rows: string[][];
  /** Which page is shown. Held by the page around this, which submits by it. */
  page: number;
  onPageChange: (page: number) => void;
  onChange: (rows: string[][]) => void;
  busy: boolean;
  /** Why each row was refused last time it was sent, aligned to `rows`. A row
   *  nothing has been said about has none. */
  errors?: (RowError | undefined)[];
  /** Cell kinds by column name; a column not named is free text. */
  types?: Record<string, CellKind>;
  /** The same table with the cells as text — the Success tab, where the rows
   *  are a record and not work. One component, so the two tabs cannot drift. */
  readOnly?: boolean;
}) {
  const t = useTranslation();
  const { pages, current, start, shown } = pageSlice(rows, page);

  // One row copied, not ten thousand: a keystroke that rebuilds every row of a
  // full file is a keystroke you can watch arrive.
  const edit = (rowIndex: number, cellIndex: number, value: string) => {
    const index = start + rowIndex;
    const row = rows[index];
    if (!row) return;
    const edited = row.slice();
    edited[cellIndex] = value;
    const next = rows.slice();
    next[index] = edited;
    onChange(next);
  };

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-fg-muted text-footnote tabular-nums">
          {t("mem.csvRowsOnPage")
            .replace("{from}", String(rows.length === 0 ? 0 : start + 1))
            .replace("{to}", String(start + shown.length))
            .replace("{total}", String(rows.length))}
        </p>
        {pages > 1 ? (
          <span className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              disabled={current === 1 || busy}
              onClick={() => onPageChange(current - 1)}
            >
              {t("common.previousPage")}
            </Button>
            <span className="text-fg-muted text-footnote tabular-nums">
              {t("rp.pageN").replace("{n}", `${current} / ${pages}`)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={current === pages || busy}
              onClick={() => onPageChange(current + 1)}
            >
              {t("common.nextPage")}
            </Button>
          </span>
        ) : null}
      </div>

      <div className="border-line overflow-x-auto rounded-xl border">
        <table className="w-max min-w-full border-collapse text-left">
          <thead className="bg-surface-inset sticky top-0 z-10">
            <tr>
              {/* The file's own line number, so a row the server rejects by
                  line can be found here without counting. */}
              <th className="text-fg-subtle text-footnote w-14 px-3 py-2 font-medium">
                {t("mem.csvLine")}
              </th>
              {header.map((column) => (
                <th
                  key={column}
                  className="text-fg-muted text-footnote px-3 py-2 font-medium whitespace-nowrap"
                >
                  {column}
                </th>
              ))}
              {/* Only when there is something to say. A column of blanks is a
                  column asking to be read. */}
              {errors?.some(Boolean) ? (
                <th className="text-fg-muted text-footnote px-3 py-2 font-medium whitespace-nowrap">
                  {t("mem.csvResult")}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {shown.map((row, rowIndex) => {
              const refused = errors?.[start + rowIndex];
              return (
                <tr
                  key={start + rowIndex}
                  className={cn(
                    "[&:not(:first-child)_td]:border-line",
                    // The row is tinted either way; when the server named the
                    // column, that one cell is marked invalid on top, so "which
                    // of these twelve boxes" has an answer.
                    refused && "bg-danger-soft",
                  )}
                >
                  <td className="text-fg-subtle text-footnote px-3 py-1 tabular-nums">
                    {start + rowIndex + 2}
                  </td>
                  {header.map((column, cellIndex) => (
                    <td
                      key={column}
                      className={readOnly ? "text-fg text-footnote px-3 py-1.5" : "px-1.5 py-1"}
                    >
                      {readOnly ? (
                        (row[cellIndex] ?? "")
                      ) : (
                        <EditableCell
                          column={column}
                          value={row[cellIndex] ?? ""}
                          kind={types?.[column]}
                          busy={busy}
                          line={start + rowIndex + 2}
                          invalid={refused?.field === columnKey(column)}
                          onEdit={(value) => edit(rowIndex, cellIndex, value)}
                        />
                      )}
                    </td>
                  ))}
                  {errors?.some(Boolean) ? (
                    <td className="text-danger text-footnote max-w-64 px-3 py-1">
                      {refused?.message ?? ""}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * One cell, drawn as its column's kind.
 *
 * A select for a fixed choice — the file may still carry another spelling
 * (`TRUE`, `1`), so a value the options do not have is kept as an extra option
 * rather than silently blanked; the server accepts those spellings, and a cell
 * must never change because it was looked at. A date as the native picker,
 * whose value is a shape the server parses. Everything else an input with the
 * keyboard hinted.
 */
function EditableCell({
  column,
  value,
  kind,
  busy,
  line,
  invalid = false,
  onEdit,
}: {
  column: string;
  value: string;
  kind: CellKind | undefined;
  busy: boolean;
  line: number;
  /** The server said this cell is the problem. `aria-invalid`, which the
   *  shared control style already draws as a danger border. */
  invalid?: boolean;
  onEdit: (value: string) => void;
}) {
  const t = useTranslation();
  const label = `${column} ${t("mem.csvLine")} ${line}`;
  if (kind?.kind === "options") {
    // Exact, not case-folded: a select's value has to match an option to the
    // letter, so a file's "Yes" beside an option "yes" still needs its own
    // entry or the box renders blank over a cell that holds a value.
    const known = kind.options.includes(value);
    return (
      <select
        value={value}
        disabled={busy}
        aria-invalid={invalid || undefined}
        aria-label={label}
        onChange={(event) => onEdit(event.target.value)}
        className={cn(controlClass, controlHeight.sm, "min-w-32")}
      >
        {kind.required && value !== "" ? null : <option value=""></option>}
        {!known && value !== "" ? <option value={value}>{value}</option> : null}
        {kind.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  // The templates' own sample dates are month-first (`9/1/2026`), which a
  // native date input cannot display — it silently shows an empty box while
  // the state still holds the value, which reads as data lost. Shown as ISO,
  // which both the input and the server's parser accept; editing writes ISO.
  const shown = kind?.kind === "date" ? (isoDate(value) ?? value) : value;
  return (
    <Input
      value={shown}
      type={kind?.kind === "date" ? "date" : "text"}
      inputMode={kind?.kind === "text" ? kind.inputMode : undefined}
      disabled={busy}
      aria-invalid={invalid || undefined}
      aria-label={label}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onEdit(event.target.value)}
      className={cn("h-8 min-w-32")}
    />
  );
}

/** `9/1/2026` or `2026-09-01` as `2026-09-01`; anything else as itself. */
function isoDate(value: string): string | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (!us) return null;
  return `${us[3]}-${us[1]!.padStart(2, "0")}-${us[2]!.padStart(2, "0")}`;
}
